import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth } from '../firebase';
import { createUserProfile, isUsernameAvailable } from '../services/userService';
import { joinLeagueByInviteCode } from '../services/leagueService';
import './Auth.css';

const USERNAME_REGEX = /^[a-zA-Z0-9_]{3,20}$/;

export default function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') === 'signup' ? 'signup' : 'signin');

  // Invite code may be stored in sessionStorage from a /join/:code redirect
  const pendingInvite = sessionStorage.getItem('pendingInviteCode');

  // ── Sign In state ──
  const [siEmail, setSiEmail] = useState('');
  const [siPassword, setSiPassword] = useState('');
  const [siError, setSiError] = useState('');
  const [siLoading, setSiLoading] = useState(false);

  // ── Sign Up state ──
  const [suUsername, setSuUsername] = useState('');
  const [suEmail, setSuEmail] = useState('');
  const [suPassword, setSuPassword] = useState('');
  const [suConfirm, setSuConfirm] = useState('');
  const [suError, setSuError] = useState('');
  const [suLoading, setSuLoading] = useState(false);

  // Username availability
  const [usernameAvailable, setUsernameAvailable] = useState(null); // null | true | false
  const [usernameChecking, setUsernameChecking] = useState(false);
  const debounceRef = useRef(null);

  // Password mismatch — detected live once both fields have value
  const passwordMismatch =
    suConfirm.length > 0 && suPassword !== suConfirm;

  // Real-time username availability check (debounced 500ms)
  useEffect(() => {
    setUsernameAvailable(null);
    if (!USERNAME_REGEX.test(suUsername)) return;
    setUsernameChecking(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const available = await isUsernameAvailable(suUsername);
      setUsernameAvailable(available);
      setUsernameChecking(false);
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [suUsername]);

  // ── Forgot password state ──
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [showReset, setShowReset] = useState(false);

  // After successful auth, process any pending invite then redirect
  async function handlePostAuth(uid) {
    if (pendingInvite) {
      try {
        await joinLeagueByInviteCode(pendingInvite, uid);
      } catch (_) {
        // Invalid or expired invite — silently ignore, user lands on selector
      } finally {
        sessionStorage.removeItem('pendingInviteCode');
      }
    }
    // Redirect — LeagueSelector handles the "exactly one league" short-circuit
    const from = location.state?.from || '/leagues';
    navigate(from, { replace: true });
  }

  // ── Sign In ──
  async function handleSignIn(e) {
    e.preventDefault();
    setSiError('');
    setSiLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, siEmail.trim(), siPassword);
      try {
        await handlePostAuth(cred.user.uid);
      } catch (_) {
        setSiError('Signed in, but something went wrong. Please refresh.');
      }
    } catch (_) {
      setSiError('Incorrect email or password.');
    } finally {
      setSiLoading(false);
    }
  }

  // ── Sign Up ──
  async function handleSignUp(e) {
    e.preventDefault();
    if (passwordMismatch) return;
    if (!USERNAME_REGEX.test(suUsername)) return;
    if (usernameAvailable === false) return;
    setSuError('');
    setSuLoading(true);
    try {
      // Re-check availability right before account creation
      const stillAvailable = await isUsernameAvailable(suUsername);
      if (!stillAvailable) {
        setSuError('That username was just taken. Please choose another.');
        setSuLoading(false);
        return;
      }
      const cred = await createUserWithEmailAndPassword(auth, suEmail.trim(), suPassword);
      await createUserProfile(cred.user.uid, {
        username: suUsername,
        email: suEmail.trim().toLowerCase(),
      });
      await handlePostAuth(cred.user.uid);
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setSuError('An account with this email already exists.');
      } else if (err.code === 'auth/weak-password') {
        setSuError('Password must be at least 6 characters.');
      } else {
        setSuError('Something went wrong. Please try again.');
      }
    } finally {
      setSuLoading(false);
    }
  }

  // ── Password Reset ──
  async function handlePasswordReset(e) {
    e.preventDefault();
    try {
      await sendPasswordResetEmail(auth, resetEmail.trim());
      setResetSent(true);
    } catch (_) {
      // Show success regardless to avoid email enumeration
      setResetSent(true);
    }
  }

  function switchTab(t) {
    setTab(t);
    setSiError('');
    setSuError('');
    setShowReset(false);
    setResetSent(false);
  }

  return (
    <div className="auth-page">
      <div className="auth-card">

        {/* Header */}
        <div className="auth-header">
          <div className="auth-logo">World Cup Pick'em</div>
          <div className="auth-subtitle">2026 FIFA World Cup · June 11 – July 19</div>
        </div>

        {/* Tab bar */}
        <div className="auth-tabs">
          <button
            className={`auth-tab${tab === 'signin' ? ' active' : ''}`}
            onClick={() => switchTab('signin')}
          >
            Sign In
          </button>
          <button
            className={`auth-tab${tab === 'signup' ? ' active' : ''}`}
            onClick={() => switchTab('signup')}
          >
            Sign Up
          </button>
        </div>

        {/* ── Sign In form ── */}
        {tab === 'signin' && !showReset && (
          <>
            <form className="auth-form" onSubmit={handleSignIn} noValidate>
              {siError && <div className="auth-error">{siError}</div>}

              <div className="auth-field">
                <label className="auth-label">Email</label>
                <input
                  className="auth-input"
                  type="email"
                  value={siEmail}
                  onChange={(e) => setSiEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                />
              </div>

              <div className="auth-field">
                <label className="auth-label">Password</label>
                <input
                  className="auth-input"
                  type="password"
                  value={siPassword}
                  onChange={(e) => setSiPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
              </div>

              <button
                type="submit"
                className="auth-btn"
                disabled={siLoading || !siEmail || !siPassword}
              >
                {siLoading ? 'Signing in…' : 'Sign In'}
              </button>
            </form>

            <div className="auth-footer">
              <button className="auth-link" onClick={() => setShowReset(true)}>
                Forgot password?
              </button>
            </div>
          </>
        )}

        {/* ── Forgot password ── */}
        {tab === 'signin' && showReset && (
          <>
            <form className="auth-form" onSubmit={handlePasswordReset} noValidate>
              {resetSent ? (
                <div className="auth-success">
                  If an account exists for that email, a reset link has been sent.
                </div>
              ) : (
                <>
                  <div className="auth-field">
                    <label className="auth-label">Email</label>
                    <input
                      className="auth-input"
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      autoComplete="email"
                    />
                  </div>
                  <button
                    type="submit"
                    className="auth-btn"
                    disabled={!resetEmail}
                  >
                    Send Reset Link
                  </button>
                </>
              )}
            </form>
            <div className="auth-footer">
              <button className="auth-link" onClick={() => { setShowReset(false); setResetSent(false); }}>
                ← Back to Sign In
              </button>
            </div>
          </>
        )}

        {/* ── Sign Up form ── */}
        {tab === 'signup' && (
          <form className="auth-form" onSubmit={handleSignUp} noValidate>
            {suError && <div className="auth-error">{suError}</div>}

            <div className="auth-field">
              <label className="auth-label">Username</label>
              <input
                className={`auth-input${suUsername && !USERNAME_REGEX.test(suUsername) ? ' error' : ''}`}
                type="text"
                value={suUsername}
                onChange={(e) => setSuUsername(e.target.value)}
                placeholder="your_username"
                maxLength={20}
                required
                autoComplete="username"
              />
              {suUsername && !USERNAME_REGEX.test(suUsername) && (
                <span className="auth-input-error">3–20 characters, letters, numbers, and underscores only</span>
              )}
              {USERNAME_REGEX.test(suUsername) && usernameChecking && (
                <span className="auth-input-hint">Checking…</span>
              )}
              {USERNAME_REGEX.test(suUsername) && !usernameChecking && usernameAvailable === true && (
                <span className="auth-input-success">✓ Available</span>
              )}
              {USERNAME_REGEX.test(suUsername) && !usernameChecking && usernameAvailable === false && (
                <span className="auth-input-error">Username already taken</span>
              )}
            </div>

            <div className="auth-field">
              <label className="auth-label">Email</label>
              <input
                className="auth-input"
                type="email"
                value={suEmail}
                onChange={(e) => setSuEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
              />
            </div>

            <div className="auth-field">
              <label className="auth-label">Password</label>
              <input
                className="auth-input"
                type="password"
                value={suPassword}
                onChange={(e) => setSuPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="new-password"
              />
            </div>

            <div className="auth-field">
              <label className="auth-label">Confirm Password</label>
              <input
                className={`auth-input${passwordMismatch ? ' error' : ''}`}
                type="password"
                value={suConfirm}
                onChange={(e) => setSuConfirm(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="new-password"
              />
              {passwordMismatch && (
                <span className="auth-input-error">Passwords do not match</span>
              )}
            </div>

            <button
              type="submit"
              className="auth-btn"
              disabled={
                suLoading ||
                passwordMismatch ||
                !USERNAME_REGEX.test(suUsername) ||
                usernameAvailable !== true ||
                !suEmail ||
                !suPassword ||
                !suConfirm
              }
            >
              {suLoading ? 'Creating account…' : 'Create Account'}
            </button>
          </form>
        )}

      </div>
    </div>
  );
}
